import { useCallback, useEffect, useRef } from "react";

type PollingSettings = {
  interval: number;
};

type PollingResult = {
  startPolling: (callback: () => Promise<void>) => void;
  stopPolling: () => void;
};

/**
 * Custom hook to execute a callback function at regular intervals
 * @param settings Polling configuration (interval, etc.)
 * @returns Functions to start and stop polling
 */
export const usePolling = ({ interval }: PollingSettings): PollingResult => {
  const isPollingActiveRef = useRef(false);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Function to start polling
  const startPolling = async (callback: () => Promise<void>) => {
    if (isPollingActiveRef.current) throw new Error("already polling");

    const poll = async () => {
      if (!isPollingActiveRef.current) return;
      try {
        await callback();
      } catch (error) {
        console.error("Polling error:", error);
      }
      // Schedule the next poll using setTimeout
      // We use setTimeout instead of setInterval because:
      // 1. It ensures the next execution only starts after the current one completes
      // 2. It prevents overlapping executions if a callback takes longer than the interval
      pollingTimeoutRef.current = setTimeout(poll, interval);
    };
    // Start polling
    isPollingActiveRef.current = true;
    poll();
  };

  // Function to stop polling
  const stopPolling = useCallback(() => {
    if (!isPollingActiveRef.current) return;
    isPollingActiveRef.current = false;
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  // Cleanup function to clear the timeout when the component unmounts
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
  };
};
